import { observer } from 'mobx-react'
import { useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { SvgIcons } from '../components/Icon/Icon'
import { Page, PageDetailsSection, PageProps, PageTitle, SplitPageMainSection } from '../components/Page/Page'
import FetchingWrapper from '../components/Page/FetchingWrapper'
import { Column } from '../components/Table/Table'
import { BodyText, BoldText } from '../components/Typography/Typography'
import { VehicleForm } from '../components/Vehicles/VehicleForm'
import { VehiclesTable } from '../components/Vehicles/VehiclesTable'
import { combinedOperationStatus } from '../models/common/OperationStatus'
import { isOversightControl } from '../stores/rootStore'
import { 
    WithVehiclesRoute,
    vehiclesPageLink, 
    vehiclesPageWithCreateNewVehicleLink, 
    getLinkForVehiclesPageWithSelectedVehicle,
    getLinkForVehiclesPageWithSelectedVehicleDetails, 
    getLinkForVehiclesPageWithSelectedVehicleParts,
    getLinkForVehiclesPageWithSelectedVehicleAndPart,
    getLinkForVehiclesPageWithSpecificVehicleAndNewPart,
    getLinkForVehiclesPageWithSpecificVehicleAndEditedPart,
    getLinkForVehiclesPageWithSelectedVehicleDiscrepancyReports,
    getLinkForVehiclesPageWithSelectedVehicleAndDiscrepancyReport, 
} from '../router'
import type { WithCenterSettingsStore, WithChecklistsStore, WithDiscrepancyReportsStore, WithPartNumbersStore, WithVehiclesStore } from '../stores/RootStoreModel'
import { theme } from '../styles/theme'
import { showNotificationForStatus, showNotificationsForStatusMap } from '../utils/NotificationUtils'


interface VehiclesPageProps extends
    PageProps,
    WithCenterSettingsStore,
    WithChecklistsStore,
    WithVehiclesStore,
    WithDiscrepancyReportsStore,
    WithPartNumbersStore,
    WithVehiclesRoute {}

export const VehiclesPage = observer(({ vehiclesStore, centerSettingsStore, checklistsStore, partNumbersStore, discrepancyReportsStore, currentRoute, ...pageProps }: VehiclesPageProps) => {

    const initializeVehicles = useCallback(async () => {
        if (checklistsStore.fetchStatus.isSuccess &&
            partNumbersStore.fetchStatus.isSuccess &&
            discrepancyReportsStore.fetchStatus.isSuccess &&
            vehiclesStore.fetchStatus.isSuccess) {
            // Don't re-fetch if already initialized. 
            // User should reload page to refetch vehicles.
            return 
        }
        await Promise.all([
            checklistsStore.fetchAndSetChecklists(), // Refresh checklists because vehicles will contain reference to those checklists
            partNumbersStore.fetchAndSetPartNumbers(), // Refresh Part numbers
            discrepancyReportsStore.fetchAndSetDiscrepancyReports(), // Fetch all discrepancy reports, to show in the reports tab
            ...(isOversightControl() ? [centerSettingsStore.fetchCenters()] : []),
        ])
        if (!checklistsStore.fetchStatus.isSuccess) {
            console.warn('Loading checklists failed, so we cannot load vehicles')
            return
        }
        if (!partNumbersStore.fetchStatus.isSuccess) {
            console.warn('Loading part numbers failed, so we cannot load vehicles')
            return
        }
        if (!discrepancyReportsStore.fetchStatus.isSuccess) {
            console.warn('Loading discrepancy reports failed, so we cannot show vehicles')
            return
        }
        return vehiclesStore.fetchAndSetVehiclesAndMaintenance() 
    }, [])

    useEffect(() => {
        void (async () => {
            await initializeVehicles()
            switch (currentRoute.name) {
                case vehiclesPageLink.name:
                    vehiclesStore.clearSelectedVehicle()
                    break
                case vehiclesPageWithCreateNewVehicleLink.name:
                    vehiclesStore.createNewVehicle()
                    break
                case getLinkForVehiclesPageWithSelectedVehicle(0).name:
                    getLinkForVehiclesPageWithSelectedVehicleParts(currentRoute.params.vehicleId).push()
                    break                    
                case getLinkForVehiclesPageWithSelectedVehicleDetails(0).name:
                    vehiclesStore.setSelectedVehicle(currentRoute.params.vehicleId)
                    void vehiclesStore.fetchVehicleParts(currentRoute.params.vehicleId) // Fetch parts already, although we inly show the part counter, not all info
                    void vehiclesStore.fetchVehicleModuleVersions(currentRoute.params.vehicleId)
                    if (isOversightControl()) void vehiclesStore.fetchVehicleRUL(currentRoute.params.vehicleId)
                    break
                case getLinkForVehiclesPageWithSelectedVehicleParts(0).name:
                    vehiclesStore.setSelectedVehicle(currentRoute.params.vehicleId)
                    void vehiclesStore.fetchVehicleParts(currentRoute.params.vehicleId)
                    break
                case getLinkForVehiclesPageWithSelectedVehicleAndPart(0, 0).name:
                    const didSelectVehicle = vehiclesStore.setSelectedVehicle(currentRoute.params.vehicleId)
                    if (!didSelectVehicle) break
                    await vehiclesStore.fetchVehicleParts(currentRoute.params.vehicleId) // Must await for fetching parts to complete before setting selected part
                    vehiclesStore.setSelectedPart(currentRoute.params.partId)
                    break
                case getLinkForVehiclesPageWithSpecificVehicleAndNewPart(0).name:
                    vehiclesStore.setSelectedVehicle(currentRoute.params.vehicleId)
                    await vehiclesStore.fetchVehicleParts(currentRoute.params.vehicleId) // Must await for fetching parts to complete before setting selected part
                    vehiclesStore.createNewPartInstance()
                    break
                case getLinkForVehiclesPageWithSpecificVehicleAndEditedPart(0, 0).name:
                    vehiclesStore.setSelectedVehicle(currentRoute.params.vehicleId)
                    await vehiclesStore.fetchVehicleParts(currentRoute.params.vehicleId) // Must await for fetching parts to complete before setting selected part
                    vehiclesStore.editPartInstanceOfSelectedVehicle(currentRoute.params.partId)
                    break
                case getLinkForVehiclesPageWithSelectedVehicleDiscrepancyReports(0).name:
                case getLinkForVehiclesPageWithSelectedVehicleAndDiscrepancyReport(0, 0).name: 
                    vehiclesStore.setSelectedVehicle(currentRoute.params.vehicleId)
                    break
                
            }
        })()
    }, [vehiclesStore, currentRoute])

    const { 
        vehicles, 
        vehicleStates, 
        vehicleParts,
        vehicleModuleVersionInformation,
        vehicleMaintenance,

        selectedVehicle, 
        vehicleCloneForEditing,
        saveEditedVehicle,
        saveStatus, 

        changeIsParkedStatus,
        canToggleSelectedVehicleIsParked,
        toggleSelectedVehicleIsParked,

        changeInServiceStatus,
        canToggleSelectedVehicleInService,
        toggleSelectedVehicleInService,

        selectedPartInstance,
        partInstanceLogs,
        fetchPartInstanceLogs,

        editedPartInstance,
        
        markPartAsUnAirworthy,
        markPartAsInspected,
        removePartFromVehicle,
        assignToDeployment: assignToDeployment,
        assignToDeploymentStatuses: assignToDeploymentStatuses,
        vehicleRUL,

        fetchModuleVersionInfoStatuses,
        fetchVehiclePartsStatuses,
        fetchVehicleMaintenanceStatuses,
        fetchVehiclesStateStatus,
        fetchPartInstanceLogStatuses,
        markPartAsInspectedStatuses,
        markPartAsUnAirworthyStatuses,
        removePartFromVehicleStatuses,
        
        saveEditedPartInstance,
        saveEditedPartInstanceStatus,
        
        setEditedPartInstanceFromSerial,
    } = vehiclesStore

    showNotificationForStatus(saveStatus, `Error saving vehicle`,`Vehicle saved successfully`, true)
    showNotificationForStatus(fetchVehiclesStateStatus, `Error fetching vehicles states`)
    showNotificationForStatus(changeIsParkedStatus, `Error changing vehicle parking state`,`Vehicle parking state changed successfully`, true)
    showNotificationForStatus(changeInServiceStatus, `Error changing vehicle service state`,`Vehicle service state changed successfully`, true)
    showNotificationForStatus(saveEditedPartInstanceStatus, `Error saving part instance`,`Part instance saved successfully`, true)
    showNotificationForStatus(discrepancyReportsStore.resolveStatus, `Error when trying to resolve discrepancy report`,`Resolved discrepancy report successfully`, true)
    
    
    showNotificationsForStatusMap(fetchModuleVersionInfoStatuses, `Error fetching module versions for vehicle`)
    showNotificationsForStatusMap(fetchVehiclePartsStatuses, `Error fetching vehicle parts for vehicle`)
    showNotificationsForStatusMap(fetchVehicleMaintenanceStatuses, `Error refreshing vehicle maintenance status for vehicle`)
    showNotificationsForStatusMap(fetchPartInstanceLogStatuses, `Error fetching part actions log for part instance`)
    showNotificationsForStatusMap(markPartAsInspectedStatuses, `Error marking part as inspected`,`Marked part as inspected`, true)
    showNotificationsForStatusMap(markPartAsUnAirworthyStatuses, `Error marking part as unairworthy`,`Marked part as unairworthy`, true)
    showNotificationsForStatusMap(removePartFromVehicleStatuses, `Error removing part from vehicle`,`Removed part from vehicle`, true)
    showNotificationsForStatusMap(assignToDeploymentStatuses, `Error transferring vehicle to center`,`Vehicle transferred successfully`, true)

    const selectedVehicleId = selectedVehicle?.id ?? null
    const aggregatedFetchStatus = combinedOperationStatus(
        checklistsStore.fetchStatus,
        partNumbersStore.fetchStatus,
        discrepancyReportsStore.fetchStatus,
        vehiclesStore.fetchStatus
    )
    return (
    <Page {...{ ...pageProps }}>
         <SplitPageMainSection {...{ pageWidth: 0.2 }}>
            <PageTitle>Vehicles</PageTitle>
            <FetchingWrapper {...{
                fetchStatus: aggregatedFetchStatus,
                inProgressTitle: 'Loading vehicles...', 
                errorMessagePrefix: 'Error loading vehicles', 
                onRetry: initializeVehicles
            }}>
                { vehicles ? 
                    <VehiclesTable {...{ 
                        vehicles, 
                        isAddingVehicle: currentRoute.name === vehiclesPageWithCreateNewVehicleLink.name,
                        vehicleStates,
                        vehicleMaintenance,
                        selectedVehicleId }}/> : 
                    <span>Oops, no vehicles present :(</span>
                }
            </FetchingWrapper>
        </SplitPageMainSection>
        <PageDetailsSection {...{ pageWidth: 0.8 }}>
            { vehicleCloneForEditing !== null ? 
                <VehicleForm {...{ 
                    currentRoute,
                    saveStatus,
                    spinnerTitle: 'Saving vehicle...',
                    editedVehicle: vehicleCloneForEditing,
                    vehicleState: selectedVehicleId !== null ? vehicleStates.get(selectedVehicleId.toString()) : undefined,
                    vehicleParts: selectedVehicleId !== null ? vehicleParts.get(selectedVehicleId.toString()) : undefined,
                    vehicleDiscrepancyReports: selectedVehicleId !== null ? discrepancyReportsStore.discrepancyReports?.filter(report => report.vehicle_id === selectedVehicleId) : undefined,
                    vehicleModuleVersionInformation: selectedVehicleId !== null ? vehicleModuleVersionInformation.get(selectedVehicleId?.toString()) : undefined,
                    centerChecklists: checklistsStore.checklists ?? [],
                    onSaveVehicle: saveEditedVehicle,

                    canToggleSelectedVehicleIsParked,
                    toggleSelectedVehicleIsParked,
            
                    canToggleSelectedVehicleInService,
                    toggleSelectedVehicleInService,
                    
                    selectedPartId: selectedPartInstance?.id ?? null,
                    
                    partInstanceLogs,
                    fetchPartInstanceLogStatuses,
                    fetchPartInstanceLogs,
                    markPartAsUnAirworthy,
                    markPartAsInspected,
                    removePartFromVehicle,

                    editedPartInstance,
                    partNumbers: partNumbersStore.partNumbers ?? [],

                    saveEditedPartInstance,
                    saveEditedPartInstanceStatus,
                    setEditedPartInstanceFromSerial,

                    resolveDiscrepancyReport: discrepancyReportsStore.resolveDiscrepancyReport,
                    assignToDeployment: assignToDeployment,
                    vehicleRUL: selectedVehicleId !== null ? vehicleRUL.get(selectedVehicleId.toString()) : undefined,
                    centers: centerSettingsStore.centers,
                }} /> 
                : 
                <NoVehicleSelected {...{ isVehicleListEmpty: vehicles?.length === 0}}/> 
            }
        </PageDetailsSection>
    </Page>
)})

interface NoVehicleSelectedProps {
    isVehicleListEmpty: boolean
}
const NoVehicleSelectedContainer = styled(Column)`
    align-items: center;
    gap: 2em;
    ${BoldText} {
        color: ${theme.colors.Black};
        text-transform: uppercase;
    }
    ${BodyText} {
        color: ${theme.colors.SecondaryGrey};
    }
`
const NoVehicleSelected = ({ isVehicleListEmpty} : NoVehicleSelectedProps) => 
    <NoVehicleSelectedContainer>
        <SvgIcons.LargeDroneSvg />
        <Column style={{alignItems: 'center'}}>
            <BoldText>{ isVehicleListEmpty ? 'No vehicles available' : 'No vehicle selected'}</BoldText>
            <BodyText>{ isVehicleListEmpty ? 'Create a new vehicle using the button below' : 'Select a vehicle to edit or add a new one'}</BodyText>
        </Column>
    </NoVehicleSelectedContainer>